/**
 * ROYAL FIX HOME — ANIMATION SYSTEM
 *
 * Centralized Framer Motion variants and transition presets. Components
 * import these instead of inlining ad-hoc animation values, so motion
 * feels consistent across the entire product and can be tuned globally
 * in one place.
 *
 * Timing philosophy: quick, confident, slightly decelerated — nothing
 * bounces or overshoots. This mirrors the "quiet luxury" design language
 * (Apple / Linear / Stripe influence) rather than a playful consumer app.
 */

export const EASE = [0.22, 1, 0.36, 1]; // "royal-ease" — matches tailwind.config.js

export const durations = {
  fast: 0.15,
  base: 0.25,
  slow: 0.4,
};

/** Full-page route transitions (used by the future <AnimatedRoute /> wrapper in Phase 4). */
export const pageTransition = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: durations.base, ease: EASE },
};

/** Card entrance — used in staggered lists (property grids, search results). */
export const cardEnter = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: durations.base, ease: EASE },
};

/** Stagger container for lists of cards. */
export const staggerContainer = {
  animate: {
    transition: { staggerChildren: 0.06 },
  },
};

/** Subtle press feedback for tappable cards. */
export const cardTap = {
  whileTap: { scale: 0.98 },
};

/** Bottom sheet slide-up (filters, premium plans, property actions). */
export const bottomSheet = {
  overlay: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: durations.fast, ease: EASE },
  },
  panel: {
    initial: { y: "100%" },
    animate: { y: 0 },
    exit: { y: "100%" },
    transition: { duration: durations.base, ease: EASE },
  },
};

/** Center modal (confirmation / success / error). */
export const modal = {
  overlay: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: durations.fast, ease: EASE },
  },
  panel: {
    initial: { opacity: 0, scale: 0.94, y: 8 },
    animate: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.96, y: 4 },
    transition: { duration: durations.base, ease: EASE },
  },
};

/** Toast notification slide-in from the top. */
export const toastVariant = {
  initial: { opacity: 0, y: -16, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -12, scale: 0.98 },
  transition: { duration: durations.fast, ease: EASE },
};

/** Icon button / primary button press feedback. */
export const buttonTap = {
  whileTap: { scale: 0.96 },
};

/** Skeleton shimmer keyframes (used with a CSS animation class, see global.css). */
export const skeletonShimmer = {
  animate: {
    backgroundPosition: ["200% 0", "-200% 0"],
    transition: { duration: 1.6, ease: "linear", repeat: Infinity },
  },
};
